import { useCallback, useEffect, useState } from 'react';
import { auth } from '../firebase';
import {
  findLatestActiveShift,
  getAssignedSites,
  getRecentIncidents,
  getRecentPatrolScans,
  getRecentSecurityShifts,
  getResolvedOrgId,
  getResolvedOrgName,
  getResolvedSiteId,
  getResolvedSiteName,
  getSecurityProfile,
  ensureFieldAuth,
  readActiveShift,
} from '../utils/securityFieldOps';

export default function useSecurityFieldContext() {
  const [state, setState] = useState({
    loading: true,
    orgId: getResolvedOrgId(),
    siteId: getResolvedSiteId(),
    user: auth.currentUser || null,
    orgName: getResolvedOrgName(),
    siteName: getResolvedSiteName(),
    profile: null,
    sites: [],
    activeShift: readActiveShift(),
    recentShifts: [],
    recentPatrols: [],
    recentIncidents: [],
    error: '',
  });

  const load = useCallback(async () => {
    try {
      const user = await ensureFieldAuth();
      const orgId = getResolvedOrgId();
      const siteId = getResolvedSiteId();

      if (!orgId) {
        setState((prev) => ({ ...prev, loading: false, user, error: 'No organization is linked yet. Open the link page first.' }));
        return;
      }

      const profile = await getSecurityProfile(orgId, user.uid);
      const sites = await getAssignedSites(orgId, profile);
      const activeShift = await findLatestActiveShift(orgId, user.uid);
      const [recentShifts, recentPatrols, recentIncidents] = await Promise.all([
        getRecentSecurityShifts(orgId, user.uid),
        getRecentPatrolScans(orgId, user.uid),
        getRecentIncidents(orgId, user.uid),
      ]);
      const resolvedSite = sites.find((site) => site.id === (siteId || activeShift?.siteId)) || sites?.[0] || null;

      setState({
        loading: false,
        orgId,
        orgName: getResolvedOrgName(),
        siteId: siteId || activeShift?.siteId || resolvedSite?.id || '',
        siteName: resolvedSite?.name || getResolvedSiteName(),
        user,
        profile,
        sites,
        activeShift,
        recentShifts,
        recentPatrols,
        recentIncidents,
        error: '',
      });
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false, error: error?.message || 'Could not load field workspace.' }));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    ...state,
    refresh: load,
  };
}
